import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import {
  useBulkAddItemsToCampaign,
  useCampaignImportCandidates,
} from "../../../../client/hooks/products/promotions.mutations";
import CampaignImportData from "./CampaignImportData";
import MoreVertIcon from "@mui/icons-material/MoreVert";

export default function CampaignTable({
  campaigns,
  isLoading,
  error,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onViewDetails,
  onEdit,
}) {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "started":
        return "success";
      case "pending":
        return "info";
      case "finished":
        return "error";
      default:
        return "default";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("pt-BR");
    } catch {
      return "N/A";
    }
  };

  const renderBenefitsCell = (benefits) => {
    if (!benefits) return (
      <Typography variant="body2" color="text.secondary">
        N/A
      </Typography>
    );
    
    const getBenefitTypeLabel = (type) => {
      switch (type) {
        case 'REBATE':
          return 'Cashback';
        case 'DISCOUNT':
          return 'Desconto';
        default:
          return type;
      }
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Chip 
          label={getBenefitTypeLabel(benefits.type)} 
          size="small"
          sx={{ 
            bgcolor: '#e8f5e8', 
            color: '#00a650',
            fontWeight: 600,
            fontSize: '0.7rem',
            height: 20,
            alignSelf: 'flex-start'
          }}
        />
        {benefits.seller_percent && (
          <Typography variant="caption" sx={{ color: '#00a650', fontWeight: 600 }}>
            Vendedor: {benefits.seller_percent}%
          </Typography>
        )}
        {benefits.meli_percent && (
          <Typography variant="caption" sx={{ color: '#ff6600', fontWeight: 600 }}>
            ML: {benefits.meli_percent}%
          </Typography>
        )}
      </Box>
    );
  };

  const { mutate: exportCandidates } = useCampaignImportCandidates();
  const { mutate: importCandidates } = useBulkAddItemsToCampaign();
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [isLoadingBtn, setIsLoadingBtn] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  const [anchorEl, setAnchorEl] = useState(null);
  const [menuCampaign, setMenuCampaign] = useState(null);

  const handleMenuOpen = (event, campaign) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuCampaign(campaign);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuCampaign(null);
  };

  function downloadBlob(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  function handleExportData(campaign) {
    setIsLoadingBtn(true);
    exportCandidates(
      { campaign },
      {
        onSuccess: (blob) => {
          const fileName = `${campaign.name !== undefined ? campaign.name : "Campanha sem nome"}.xls`;
          downloadBlob(blob, fileName);
        },
        onSettled: () => {
          setIsLoadingBtn(false);
        },
      }
    );
  }
  function handleImporData(campaign) {
    setSelectedCampaign(campaign);
    setImportDialogOpen(true);
  }

  const handleFileSelected = async (file, campaign) => {
    setIsLoadingBtn(true);
    try {
      const data = await file.arrayBuffer(); // lê como binário
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      // Monta estrutura final
      const payload = {
        promotion_type: campaign.type,
        items: json.map((row) => ({
          item_id: String(row["Item ID"]).trim(),
          deal_price: Number(
            row["Suggested Discounted Price"] ||
              row["deal_price"] ||
              row["preco"] ||
              row["preço"] ||
              row["price"] ||
              row["Price"] ||
              0
          ),
          offer_id: String(row["Offer Id"]).trim() || null,
        })),
      };
      importCandidates({ promotionId: campaign.id, data: payload });
      // Aqui você pode fazer o POST com `api.post("/rota", payload)`
    } catch (err) {
      console.error("Erro ao processar o arquivo:", err);
    } finally {
      setIsLoadingBtn(false);
    }
  };

  const filteredCampaigns = campaigns.results || [];

  return (
    <Card
      sx={{
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        borderRadius: 2,
      }}
    >
      <CardContent sx={{ p: 0 }}>
        {error && (
          <Box sx={{ p: 3 }}>
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              Erro ao carregar campanhas: {error.message}
            </Alert>
          </Box>
        )}

        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              p: 6,
            }}
          >
            <CircularProgress sx={{ color: "#3483fa" }} />
          </Box>
        ) : (
          <>
            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: "grey.50" }}>
                    <TableCell sx={{ fontWeight: 600, color: "grey.700" }}>
                      ID
                    </TableCell>{" "}
                    <TableCell sx={{ fontWeight: 600, color: "grey.700" }}>
                      Nome
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "grey.700" }}>
                      Tipo
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "grey.700" }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "grey.700" }}>
                      Benefits
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "grey.700" }}>
                      Data Início
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "grey.700" }}>
                      Data Fim
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "grey.700" }}>
                      Ações
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCampaigns.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                        <Typography
                          variant="body1"
                          color="text.secondary"
                          sx={{ fontSize: "1.1rem" }}
                        >
                          Nenhuma campanha encontrada
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCampaigns.map((campaign) => (
                      <TableRow
                        key={campaign.id}
                        hover
                        sx={{
                          "&:hover": {
                            bgcolor: "rgba(52, 131, 250, 0.04)",
                          },
                        }}
                      >
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontFamily="monospace"
                            sx={{
                              color: "#3483fa",
                              fontWeight: 500,
                            }}
                          >
                            {campaign.id || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            sx={{ color: "grey.800" }}
                          >
                            {campaign.name || "Sem nome"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ color: "grey.700" }}
                          >
                            {campaign.type || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={campaign.status || "Desconhecido"}
                            color={getStatusColor(campaign.status)}
                            size="small"
                            sx={{
                              fontWeight: 500,
                              borderRadius: 2,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {renderBenefitsCell(campaign.benefits)}
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ color: "grey.700" }}
                          >
                            {formatDate(campaign.start_date)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ color: "grey.700" }}
                          >
                            {formatDate(campaign.finish_date)}
                          </Typography>{" "}
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                            }}
                          >
                            <Tooltip title="Ver detalhes">
                              <IconButton
                                size="small"
                                onClick={() => onViewDetails(campaign)}
                                sx={{
                                  color: "#3483fa",
                                  "&:hover": {
                                    bgcolor: "rgba(52, 131, 250, 0.1)",
                                  },
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Editar">
                              <IconButton
                                size="small"
                                onClick={() => onEdit(campaign)}
                                sx={{
                                  color: "grey.600",
                                  "&:hover": {
                                    bgcolor: "grey.100",
                                  },
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Mais ações">
                              <IconButton
                                size="small"
                                onClick={(e) => handleMenuOpen(e, campaign)}
                                sx={{
                                  bgcolor: "grey.100",
                                  "&:hover": { bgcolor: "grey.200" },
                                }}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Menu
                              anchorEl={anchorEl}
                              open={
                                Boolean(anchorEl) &&
                                menuCampaign?.id === campaign.id
                              }
                              onClose={handleMenuClose}
                              anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "right",
                              }}
                              transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                              }}
                            >
                              <MenuItem
                                disabled={isLoadingBtn}
                                onClick={() => {
                                  if (!isLoadingBtn) {
                                    handleExportData(menuCampaign);
                                    handleMenuClose();
                                  }
                                }}
                              >
                                {isLoadingBtn ? (
                                  <>
                                    <CircularProgress
                                      size={18}
                                      sx={{ mr: 1 }}
                                    />
                                    Aguardado...
                                  </>
                                ) : (
                                  "Exportar campanha"
                                )}
                              </MenuItem>

                              <MenuItem
                                disabled={isLoadingBtn}
                                onClick={() => {
                                  if (!isLoadingBtn) {
                                    handleImporData(menuCampaign);
                                    handleMenuClose();
                                  }
                                }}
                              >
                                {isLoadingBtn ? (
                                  <>
                                    <CircularProgress
                                      size={18}
                                      sx={{ mr: 1 }}
                                    />
                                    Aguardando...
                                  </>
                                ) : (
                                  "Aderir em Massa"
                                )}
                              </MenuItem>
                            </Menu>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>{" "}
              <CampaignImportData
                open={importDialogOpen}
                onClose={() => setImportDialogOpen(false)}
                onFileSelected={handleFileSelected}
                campaign={selectedCampaign}
              />
            </TableContainer>
            <TablePagination
              component="div"
              count={campaigns?.results?.length || 0}
              page={page}
              onPageChange={(event, newPage) => onPageChange(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) =>
                onRowsPerPageChange(parseInt(event.target.value, 10))
              }
              labelRowsPerPage="Linhas por página:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
              }
              sx={{
                borderTop: "1px solid",
                borderColor: "grey.200",
                bgcolor: "grey.50",
              }}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}
